import { Button, Flex, Modal, message } from "antd";
import "./index.css"
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useCreateProgramBookingByMemberIdAndCreateProgramBookingWebRequest } from "../model";
import { ReactComponent as Close } from '../../../../../assets/images/icon/close.svg';
import { Fragment, useEffect, useRef, useState } from "react";
import { isIOS } from "react-device-detect";
import { DividerThin } from "../../../../../shared";
import LoadingBig from '../../../../../assets/images/lottie/loading_big.json';
import Lottie from "lottie-react";
import { confirmedRequestDetail } from "../../../../../utils/gtm";

interface IProps {
    isOpen: boolean
    setIsOpen: Function
    bookingData: IBookingData
    contentHeight?: number
    isPaid: boolean
}

const ConfirmBottomSheet = ({ isOpen, setIsOpen, bookingData, contentHeight, isPaid }: IProps) => {
    const minWidth = window.innerWidth <= 480 ? '100vw' : '480px';
    const marginInline = window.innerWidth <= 480 ? '0' : (window.innerWidth - 480) / 2;
    const [targetHeight, setTargetHeight] = useState(contentHeight);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const createProgramBookingByMemberIdAndCreateProgramBookingWebRequest = useCreateProgramBookingByMemberIdAndCreateProgramBookingWebRequest()

    const formatDate = (dateString: string): string => {
        return dayjs(dateString).format("YYYY.MM.DD (ddd)");
    };

    const onClickCreateButton = () => {
        const createParams: CreateProgramBookingWebRequest = {
            programId: bookingData.programId,
            programBookingTicketList: bookingData.ticketList.map((ticket: IProgramBookingTicket) => {
                return {
                    programTicketId: Number(ticket.id),
                    count: ticket.count
                }
            }),
            programEventId: bookingData.eventId,
            programBookingSelectOptionList: bookingData.optionList.selectOptionList.map((selectOption: ISelectOption) => ({
                programSelectOptionId: Number(selectOption.id),
                programSelectOptionItemIdList: [Number(selectOption.selectedOptionId)]
            })),
            programBookingSubjectOptionList: bookingData.optionList.subjectOptionList.map((subjectOption: ISubjectOption) => ({
                programSubjectOptionId: Number(subjectOption.id),
                content: subjectOption.value
            }))
        }
        const title = bookingData.title;
        const ticketName = isPaid ? bookingData.ticketList.map(q => q.name).join(',') : '-';
        const quantity = bookingData.ticketList.reduce((acc, ticket) => acc + ticket.count, 0)
        const price = isPaid ? bookingData.ticketList.reduce((acc, ticket) => acc + (ticket.payAmount * ticket.count), 0) : 0
        const date = bookingData.date;
        const startTime = bookingData.startTime;

        confirmedRequestDetail(title, ticketName, quantity, price, date, startTime)

        createProgramBookingByMemberIdAndCreateProgramBookingWebRequest.mutate(createParams, {
            onSuccess: res => {
                if (!res.data.errors) {
                    navigate('/wellness-program/book/confirm', { replace: true, state: { title: title, ticketName: ticketName, quantity: quantity, price: price, date: date, startTime: startTime, isPaid: isPaid, programBookingId: res.data.data.createProgramBookingByMemberIdAndCreateProgramBookingWebRequest } })
                } else {
                    // message.error('에러가 발생했습니다 개발팀에 문의해주세요')
                }
            }
        })
    }

    useEffect(() => {
        if (contentRef.current) {
            const height = contentRef.current.offsetHeight + 20;
            setTargetHeight(window.innerHeight < height ? window.innerHeight : height);
        }
    }, []);

    return <>
        <Modal transitionName="custom-modal"
            style={{ top: `calc(100vh - ${targetHeight}px)`, bottom: 0, padding: 0, margin: 0 }}
            styles={{ wrapper: { overflow: 'hidden' }, content: { minWidth: minWidth, maxHeight: '100vh', height: `${targetHeight}px`, overflow: "scroll", borderRadius: "16px 16px 0 0", padding: "20px 20px 0", marginInline: marginInline + 'px' } }}
            open={isOpen} closeIcon={null} footer={null} onCancel={() => setIsOpen(false)}>
            <div ref={contentRef}>
                <div className="title/t1" style={{ color: '#111111', marginBottom: 'var(--space20)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>예약 정보</div><Close width={16} height={16} stroke="#AEAEAE" style={{ padding: 'var(--space8)', cursor: 'pointer' }} onClick={() => setIsOpen(false)} /></div>
                <div style={{ backgroundColor: 'var(--secondarys100)', padding: 12, borderRadius: 'var(--radius4)' }}>
                    <Flex align='center' gap={16}>
                        <img style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '5px' }} src={bookingData.imageUrl} alt="프로그램 이미지"></img>
                        <div>
                            <div className="sub-title/st3">{bookingData.title}</div>
                            <div className="body/b1 secondary/s500" style={{ marginTop: 4 }}>{bookingData.locationName}</div>
                        </div>
                    </Flex>
                </div>
                <div style={{ marginTop: 20 }}>
                    <Flex justify="space-between" style={{ padding: '12px 0' }}>
                        <div className="body/b2 secondary/s600">예약 일시</div>
                        <div className="body/b2">{`${formatDate(bookingData.date.toString())}  ${bookingData.startTime}`}{ }</div>
                    </Flex>
                </div>
                <DividerThin style={{ marginBlock: 4 }} />

                <div style={{ padding: '12px 0' }}>
                    {bookingData.ticketList.filter((ticket: IProgramBookingTicket) => ticket.count > 0).map((ticket: IProgramBookingTicket) =>
                        <Flex key={ticket.id} justify="space-between" style={{ padding: '4px 0' }}>
                            <div className="body/b2 secondary/s600">{ticket.name === 'free' ? '인원' : ticket.name}</div>
                            <div>{ticket.count}명</div>
                        </Flex>
                    )}
                </div>
                {(bookingData.optionList.selectOptionList.length !== 0 || bookingData.optionList.subjectOptionList.length !== 0) && <DividerThin style={{ marginBlock: 4 }} />}
                <div>
                    {bookingData.optionList.selectOptionList.map((selectOption, idx, arr) =>
                        <Fragment key={selectOption.id}>
                            <Flex gap={8} justify="space-between" style={{ padding: '12px 0 16px' }}>
                                <div className="body/b2 secondary/s600" style={{ maxWidth: '50%' }}>{selectOption.name}</div>
                                <div className="body/b2" style={{ maxWidth: '50%' }}>{selectOption.selectedOptionName}</div>
                            </Flex>
                            {(idx !== arr.length - 1) && bookingData.optionList.subjectOptionList.length !== 0 && <DividerThin style={{ marginBlock: 4 }} />}
                        </Fragment>
                    )}
                </div>

                <div>
                    {bookingData.optionList.subjectOptionList.map((subjectOption, idx, arr) =>
                        <Fragment key={subjectOption.id}>
                            <Flex gap={8} justify="space-between" style={{ padding: '12px 0 16px' }}>
                                <div className="body/b2 secondary/s600" style={{ maxWidth: '50%' }}>{subjectOption.name}</div>
                                <div className="body/b2" style={{ maxWidth: '50%' }}>{subjectOption.value}</div>
                            </Flex>
                            {(idx !== arr.length - 1) && <DividerThin style={{ marginBlock: 4 }} />}
                        </Fragment>
                    )}
                </div>
                {isPaid &&
                    <div style={{ borderTop: '1px solid black' }}>
                        <Flex justify="space-between" style={{ padding: '16px 0 12px', marginTop: '4px' }}>
                            <div className="sub-title/st2 secondary/s850">합계</div>
                            <div className="sub-title/st2 secondary/s850">{bookingData.ticketList.filter((ticket: IProgramBookingTicket) => ticket.count > 0).reduce((sum, ticket) => sum + (ticket.count * ticket.payAmount), 0).toLocaleString()}원</div>
                        </Flex>
                        <div className="caption/c1 secondary/s500" style={{ textAlign: 'right' }}>※ 예약 확정 시 결제가 완료됩니다.</div>
                    </div>}



                <div style={{ paddingTop: 20, paddingBottom: isIOS ? '46px' : '20px' }}>
                    <Button
                        loading={createProgramBookingByMemberIdAndCreateProgramBookingWebRequest.isPending}
                        disabled={createProgramBookingByMemberIdAndCreateProgramBookingWebRequest.isPending}
                        color="default"
                        variant="solid"
                        className="sub-title/st3 base/white"
                        style={{ width: '100%', height: '51px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderRadius: 4, backgroundColor: createProgramBookingByMemberIdAndCreateProgramBookingWebRequest.isPending ? 'var(--secondarys200)' : '' }}
                        onClick={() => onClickCreateButton()}>
                        {createProgramBookingByMemberIdAndCreateProgramBookingWebRequest.isPending ? '신청중' : '신청하기'}
                    </Button>
                </div>
            </div>
        </Modal >
    </>
}

export { ConfirmBottomSheet };


